/**
 * Lepený dielec — vyskočí, keď používateľ vyberie hrúbku so split rule
 * (napr. 36 mm = 18+18).
 *
 * „Rozdeliť na vrstvy": dielec OSTÁVA (rodič nesie rozmery a hrany —
 * olepuje sa celý sendvič) a pod neho sa zabalia vrstvy
 * `A2a`, `A2b`, … (KOLO 134; dosky/materiál, operácia lep_nc, bez
 * hrán). V kusovníku figurujú vrstvy, doska rodiča sa nepočíta.
 *
 * Každá vrstva má VLASTNÝ materiál v hrúbke vrstvy: default sa
 * auto-matchne podľa dekoru rodiča (napr. dub 18 aj dub 10), ale dá sa
 * zmeniť — spodná zlepená plocha nemusí byť z drahej lesklej dosky.
 *
 * „Nechať jeden dielec": len zmena hrúbky, žiadne vrstvy.
 *
 * Vrstvy sú triedy (18 = doska 18,0–18,9 mm) a nesú skutočnú hrúbku
 * dosky; dielec má ich súčet — z AL05 18,7 je 36 = **37,4** (KOLO 188,
 * `lib/splitRules`). Server to po založení vrstiev drží rovnako.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useDeleteDielec,
  useQuickCreateDielec,
  useUpdateDielec,
} from "@/api/hooks/useDielce";
import { useCennikMaterialy, useMaterialDetail } from "@/api/hooks/useCennik";
import { useZakazkaDetail } from "@/api/hooks/useZakazky";
import { MaterialSelect } from "@/components/slot/MaterialSelect";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import {
  comboKey,
  formatMm,
  hrubkaLepeneho,
  kombinacieZMaterialu,
  trieda,
} from "@/lib/splitRules";
import { formatEur } from "@/lib/format";
import type {
  Dielec,
  DielecCreate,
  Material,
  SplitRule,
  Varianta,
} from "@/api/schemas";
import {
  jeLepiaciPostup,
  OPERACIA_LEPENA_VRSTVA,
  POSTUP_SENDVICA,
} from "@/lib/operacie";
import { kodVrstvy } from "@/lib/dielecKod";

/** Minimálny tvar materiálu vrstvy (Material aj MaterialSummary). */
type MatLike = Pick<Material, "id" | "nazov" | "nakup_cena" | "mj"> & {
  z_mm?: string | number | null;
  dekor_kod?: string | null;
};

export interface LepenyConvertDialogProps {
  open: boolean;
  zakazkaId: string;
  dielec: Dielec;
  /** Split rule pre zvolenú hrúbku (napr. 36 → [[18,18]]). */
  rule: SplitRule;
  /** Hrúbka, ktorú user zadal (37.4) — pre „Nechať jeden dielec". */
  zvolenaZ?: string;
  /** Existujúce vrstvy dielca — pri re-splite sa nahradia. */
  vrstvy?: Dielec[];
  onClose: () => void;
}

/** Materiál vrstvy — slot (ak sedí hrúbka) alebo priamy materiál. */
interface VrstvaMaterial {
  slotId: string | null;
  directId: string | null;
}

/** Hrúbka materiálu ako číslo, alebo null. */
function zMaterialu(m: { z_mm?: string | number | null } | null | undefined): number | null {
  if (m?.z_mm == null) return null;
  const z = parseFloat(String(m.z_mm));
  return Number.isFinite(z) ? z : null;
}

/** Payload vrstvy — kópia rodiča bez hrán, s operáciou lep_nc. */
function vrstvaPayload(
  parent: Dielec,
  i: number,
  layerZ: number,
  material: VrstvaMaterial,
): DielecCreate {
  const base = parent.nazov_strojovy ?? parent.kod;
  return {
    kod: kodVrstvy(parent.kod, i),
    nazov_strojovy: `${base}_lep${i}`,
    nazov_volny: null,
    kategoria: parent.kategoria ?? null,
    pocet_ks: parent.pocet_ks,
    material_slot_id: material.slotId,
    material_id_direct: material.directId,
    x_mm: parent.x_mm ?? null,
    y_mm: parent.y_mm ?? null,
    z_mm: String(layerZ),
    nadmiera_x_mm: parent.nadmiera_x_mm,
    nadmiera_y_mm: parent.nadmiera_y_mm,
    nadmiera_aktivna: parent.nadmiera_aktivna,
    smer_vlakien: parent.smer_vlakien,
    // Vrstvy hrany nemajú — olepuje sa celý sendvič (hrany na rodičovi)
    hrana_x1_id: null,
    hrana_x2_id: null,
    hrana_y1_id: null,
    hrana_y2_id: null,
    hrana_x1_auto: false,
    hrana_x2_auto: false,
    hrana_y1_auto: false,
    hrana_y2_auto: false,
    operacia: OPERACIA_LEPENA_VRSTVA,
    poznamky: null,
    poradie: (parent.poradie ?? 0) + i,
    parent_dielec_id: parent.id,
  };
}

export function LepenyConvertDialog({
  open,
  zakazkaId,
  dielec,
  rule,
  zvolenaZ,
  vrstvy = [],
  onClose,
}: LepenyConvertDialogProps): JSX.Element {
  const updateMutation = useUpdateDielec(zakazkaId);
  const createMutation = useQuickCreateDielec(zakazkaId);
  const deleteMutation = useDeleteDielec(zakazkaId);

  // "rozdelit" | "alt:<18+18>" | "nechat"
  const [choice, setChoice] = useState<string>("rozdelit");
  const [busy, setBusy] = useState(false);
  // Ručné prepísanie materiálu vrstvy (index → materiál | null)
  const [overrides, setOverrides] = useState<Record<number, MatLike | null>>({});
  const [editingLayer, setEditingLayer] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      setChoice("rozdelit");
      setOverrides({});
      setEditingLayer(null);
    }
  }, [open, rule.hrubka_mm]);

  // ── Materiál rodiča (slot cez zakazka detail cache / direct fetch) ──
  const zakazkaQuery = useZakazkaDetail(zakazkaId);
  const slotMaterial = useMemo(() => {
    if (!dielec.material_slot_id) return null;
    const varianty = (zakazkaQuery.data?.data.varianty ?? []) as Varianta[];
    for (const v of varianty) {
      for (const s of v.sloty ?? []) {
        if (s.id === dielec.material_slot_id) return s.material ?? null;
      }
    }
    return null;
  }, [zakazkaQuery.data, dielec.material_slot_id]);
  const directQuery = useMaterialDetail(
    dielec.material_id_direct ?? undefined,
  );
  const parentMaterial: MatLike | null =
    slotMaterial ?? directQuery.data?.data ?? null;

  // ── Auto-match: rovnaký dekor v hrúbkach vrstiev ──
  const dekorKandidatiQuery = useCennikMaterialy({
    dekor_kod: parentMaterial?.dekor_kod ?? undefined,
    druh: "plosny",
    // Vrstvy sú dosky; appka ich vyberá sama, takže v rozsahu pickera
    sekcia: "materialy",
    vyber: true,
    per_page: 100,
  });
  const dekorKandidati = useMemo(
    () =>
      parentMaterial?.dekor_kod ? (dekorKandidatiQuery.data?.data ?? []) : [],
    [dekorKandidatiQuery.data, parentMaterial?.dekor_kod],
  );

  // Len kombinácie, v ktorých je materiál rodiča jednou vrstvou (KOLO 162)
  // — z 18 mm nie 12 + 24, podľa triedy (18,7 je 18); kým sa materiál
  // nenačíta, všetky
  const parentZ = zMaterialu(parentMaterial);
  const zMaterialuRodica =
    parentZ != null ? kombinacieZMaterialu(rule, parentZ) : [];
  const kombinacie =
    zMaterialuRodica.length > 0 ? zMaterialuRodica : rule.kombinacie;
  const defaultCombo = kombinacie[0] ?? [rule.hrubka_mm];
  const altCombos = kombinacie.slice(1);

  function chosenLayers(): number[] {
    if (choice.startsWith("alt:")) {
      const picked = choice
        .slice(4)
        .split("+")
        .map((n) => parseInt(n, 10))
        .filter((n) => Number.isFinite(n) && n > 0);
      return picked.length > 0 ? picked : defaultCombo;
    }
    return defaultCombo;
  }

  /**
   * Default materiál vrstvy: rodičov (ak sedí trieda) alebo dekor v triede
   * vrstvy, najbližší k nej — ako server `material_vrstvy` (KOLO 188).
   */
  function autoMaterialForLayer(layerZ: number): MatLike | null {
    if (parentMaterial && parentZ != null && trieda(parentZ) === layerZ) {
      return parentMaterial;
    }
    let najblizsi: MatLike | null = null;
    for (const m of dekorKandidati) {
      const z = zMaterialu(m);
      if (z == null || trieda(z) !== layerZ) continue;
      const nz = zMaterialu(najblizsi);
      if (nz == null || Math.abs(z - layerZ) < Math.abs(nz - layerZ)) najblizsi = m;
    }
    return najblizsi;
  }

  function effectiveMaterial(i: number, layerZ: number): MatLike | null {
    if (i in overrides) return overrides[i] ?? null;
    return autoMaterialForLayer(layerZ);
  }

  /** Skutočná hrúbka vrstvy: doska v triede vrstvy (18,7), inak trieda. */
  function hrubkaVrstvy(i: number, layerZ: number): number {
    const z = zMaterialu(effectiveMaterial(i, layerZ));
    return z != null && trieda(z) === layerZ ? z : layerZ;
  }

  /** Slot vs direct: rodičov slot len ak hrúbka materiálu slotu sedí. */
  function vrstvaMaterialRef(i: number, layerZ: number): VrstvaMaterial {
    const mat = effectiveMaterial(i, layerZ);
    if (mat === null) return { slotId: null, directId: null };
    const slotZ = zMaterialu(slotMaterial);
    if (
      dielec.material_slot_id &&
      slotMaterial &&
      mat.id === slotMaterial.id &&
      slotZ != null &&
      trieda(slotZ) === layerZ &&
      !(i in overrides)
    ) {
      return { slotId: dielec.material_slot_id, directId: null };
    }
    return { slotId: null, directId: mat.id };
  }

  async function confirm(): Promise<void> {
    setBusy(true);
    try {
      // Rodič vždy dostane novú hrúbku (36) — dielec OSTÁVA.
      // Sendvič sa vždy lepí (KOLO 138) — iný než lepiaci postup sa
      // prepne na lep_nc_olep; „nechať" (jeden dielec) postup nemení
      const lepit = choice !== "nechat" && !jeLepiaciPostup(dielec.operacia);
      const odpoved = await updateMutation.mutateAsync({
        id: dielec.id,
        data: {
          z_mm: String(vyslednaZ),
          ...(lepit ? { operacia: POSTUP_SENDVICA } : {}),
        },
      });
      // Vrstvy berú rozmer rodiča **po** zmene hrúbky: s novou hrúbkou mu
      // backend vymení hrany do novej šírky (KOLO 135) a rozmer s hranou
      // narastie (801 → 802). So starým rozmerom by vrstvy vznikli ako
      // „vlastný rozmer" a rodiča by už nenasledovali (KOLO 138).
      const rodic = { ...dielec, ...(odpoved?.data ?? {}) };
      // Staré vrstvy preč (re-split alebo „nechať")
      for (const v of vrstvy) {
        await deleteMutation.mutateAsync(v.id);
      }
      if (choice === "nechat") {
        toast({
          title: `Hrúbka ${formatMm(vyslednaZ)} mm — jeden dielec (bez vrstiev)`,
        });
        onClose();
        return;
      }
      const layers = chosenLayers();
      for (const [i, layerZ] of layers.entries()) {
        await createMutation.mutateAsync({
          zostavaId: dielec.zostava_id,
          data: vrstvaPayload(
            rodic,
            i + 1,
            hrubkaVrstvy(i, layerZ),
            vrstvaMaterialRef(i, layerZ),
          ),
        });
      }
      // Lepenie (vnorený riadok lep_striek, €/m² × škáry) založí server
      // s prvou vrstvou (KOLO 226) — dialóg ho dovtedy posielal sám a pri
      // prerozdelení vznikalo druhé. Zmazaním starých vrstiev server
      // vrátil postup bez lepenia a nová vrstva dala `lep_nc_olep` —
      // iný lepiaci postup (`lep_nc_olep_nc`) sa vráti tu.
      const postup = rodic.operacia ?? null;
      if (
        vrstvy.length > 0 &&
        jeLepiaciPostup(postup) &&
        postup !== POSTUP_SENDVICA
      ) {
        await updateMutation.mutateAsync({ id: dielec.id, data: { operacia: postup } });
      }
      toast({
        title: `Lepený dielec ${formatMm(vyslednaZ)} mm — ${layers.length} vrstvy`,
        description: layers
          .map((z, i) => `${kodVrstvy(dielec.kod, i + 1)} (${formatMm(hrubkaVrstvy(i, z))} mm)`)
          .join(", ") +
          ` · lepenie ${Math.max(1, layers.length - 1)}× sa pripočítava k cene`,
      });
      onClose();
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba pri ukladaní",
        description: extractErrorMessage(err),
      });
    } finally {
      setBusy(false);
    }
  }

  const altDropdownValue = choice.startsWith("alt:")
    ? choice.slice(4)
    : comboKey(altCombos[0] ?? defaultCombo);

  const rozdelene = choice !== "nechat";
  const layers = chosenLayers();
  // Skutočná hrúbka dielca: súčet vrstiev (2 × 18,7 = 37,4), pri „nechať"
  // zadaná hrúbka (KOLO 188)
  const skutocneVrstvy = layers.map((z, i) => hrubkaVrstvy(i, z));
  const zadanaZ = zvolenaZ != null && zvolenaZ !== "" ? parseFloat(zvolenaZ) : NaN;
  const vyslednaZ = rozdelene
    ? (hrubkaLepeneho(skutocneVrstvy, rule.hrubka_mm) ?? rule.hrubka_mm)
    : Number.isFinite(zadanaZ)
      ? zadanaZ
      : rule.hrubka_mm;
  const nadpisZ = Number.isFinite(zadanaZ) ? zadanaZ : rule.hrubka_mm;
  // Súčet tried nad pravidlom (35 = 18 + 18) — po zlepení sa frézuje
  const frezuje =
    rozdelene && skutocneVrstvy.reduce((s, z) => s + trieda(z), 0) > rule.hrubka_mm;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Hrúbka {formatMm(nadpisZ)} mm sa lepí</DialogTitle>
          <DialogDescription className="sr-only">
            Dielec tejto hrúbky sa vyrába zlepením tenších dosiek — tu si vyberieš, akých.
          </DialogDescription>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">
          Dielec {dielec.kod} ostane jeden riadok (rozmery + hrany). Pri
          rozdelení sa pod neho zabalia vrstvy — samostatné dosky
          v kusovníku s operáciou Lepenie + CNC.
        </p>
        <div className="space-y-2 text-sm">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="lepeny-choice"
              checked={choice === "rozdelit"}
              onChange={() => setChoice("rozdelit")}
              className="accent-primary"
            />
            Rozdeliť na vrstvy — {defaultCombo.join(" + ")} mm (
            {defaultCombo.length} vrstvy)
          </label>
          {altCombos.length > 0 ? (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="lepeny-choice"
                checked={choice.startsWith("alt:")}
                onChange={() => setChoice(`alt:${altDropdownValue}`)}
                className="accent-primary"
              />
              Iná kombinácia:
              <Select
                value={altDropdownValue}
                onValueChange={(v) => setChoice(`alt:${v}`)}
              >
                <SelectTrigger className="h-7 w-32 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {altCombos.map((combo) => (
                    <SelectItem key={comboKey(combo)} value={comboKey(combo)}>
                      {combo.join(" + ")} mm
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          ) : null}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="lepeny-choice"
              checked={choice === "nechat"}
              onChange={() => setChoice("nechat")}
              className="accent-primary"
            />
            Nechať jeden dielec ({formatMm(Number.isFinite(zadanaZ) ? zadanaZ : rule.hrubka_mm)} mm,
            bez vrstiev)
          </label>
        </div>

        {rozdelene ? (
          <div className="space-y-1.5 rounded-md border p-2.5">
            <p className="text-xs font-medium">
              Materiály vrstiev{" "}
              <span className="font-normal text-muted-foreground">
                (vrstva 1 = pohľadová; spodná môže byť lacnejšia doska)
              </span>
            </p>
            {layers.map((layerZ, i) => {
              const mat = effectiveMaterial(i, layerZ);
              return (
                <div key={`${i}-${layerZ}`} className="space-y-1">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="w-24 shrink-0 font-mono">
                      Vrstva {i + 1} · {formatMm(hrubkaVrstvy(i, layerZ))} mm
                    </span>
                    {mat ? (
                      <span className="min-w-0 flex-1 truncate" title={mat.nazov}>
                        {mat.nazov}
                        <span className="ml-1 font-mono text-muted-foreground">
                          {formatEur(mat.nakup_cena)}/{mat.mj}
                        </span>
                      </span>
                    ) : (
                      <span className="flex-1 text-amber-700">
                        bez materiálu — dekor {parentMaterial?.dekor_kod ?? "?"}{" "}
                        v {layerZ} mm nie je v cenníku
                      </span>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-6 px-2 text-[11px]"
                      onClick={() =>
                        setEditingLayer(editingLayer === i ? null : i)
                      }
                    >
                      {editingLayer === i ? "Hotovo" : "Zmeniť"}
                    </Button>
                  </div>
                  {editingLayer === i ? (
                    <MaterialSelect
                      value={mat?.id ?? null}
                      onChange={(_, material) => {
                        setOverrides((prev) => ({
                          ...prev,
                          [i]: material ?? null,
                        }));
                        if (material) setEditingLayer(null);
                      }}
                      druh="plosny"
                      sekcia="materialy"
                      triedaHrubky={layerZ}
                      placeholder={`Doska ${layerZ} mm…`}
                    />
                  ) : null}
                </div>
              );
            })}
            <p className="pt-1 text-xs" data-testid="hrubka-lepeneho">
              Dielec bude mať{" "}
              <span className="font-medium">{formatMm(vyslednaZ)} mm</span> (
              {skutocneVrstvy.map(formatMm).join(" + ")}
              {frezuje ? `, frézuje sa na ${formatMm(vyslednaZ)}` : ""})
            </p>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Zrušiť
          </Button>
          <Button onClick={() => void confirm()} disabled={busy}>
            {busy ? "Ukladám…" : "Potvrdiť"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
